import { useCallback, useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Button } from '@mui/material';
import {
  getBasicWarehouses,
  useDispatch,
  useSelector,
  warehousesSelectors,
} from '../../state';
import { BasicDialog, ProcessContainer, SelectInput } from '..';
import { useIsMobile } from '../../hooks';
import { MobileModalForm } from '../../themes/mobile/MobileModalForm';
import { InputChangeEventType, MouseChangeEventType } from '../../types';

interface Props {
  handleSelect: (warehouseId: string | number) => any;
  /**
   * If rendering in a modal
   */
  modalOpen?: boolean;
  /**
   * If rendering in a modal
   */
  handleModalClose?: () => any;
}

interface FormProps {
  handleChange: (e: InputChangeEventType) => void;
  warehouseId: string;
  error: string | null;
  handleSubmit?: (
    e?: React.FormEvent<HTMLFormElement> | MouseChangeEventType,
  ) => void;
}

export function SelectWarehouse({
  handleSelect,
  modalOpen,
  handleModalClose,
}: Props) {
  const isMobile = useIsMobile();
  const dispatch = useDispatch();

  const [warehouseId, setWarehouseId] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async function () {
      await dispatch(getBasicWarehouses());
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = useCallback((e: InputChangeEventType) => {
    setError(null);
    setWarehouseId(e.target.value);
  }, []);

  const handleSubmit = useCallback(
    (e?: React.FormEvent<HTMLFormElement> | MouseChangeEventType) => {
      e?.preventDefault();

      if (!warehouseId) {
        setError('Warehouse is required');
        return;
      }
      handleSelect(warehouseId);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [warehouseId],
  );

  if (!handleModalClose) {
    return (
      <RenderCard
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        warehouseId={warehouseId}
        error={error}
      />
    );
  }
  if (isMobile) {
    return (
      <MobileModalForm
        open={modalOpen}
        title='Select warehouse'
        handleClose={handleModalClose}
        callback={handleSubmit}
      >
        <RenderInput
          handleChange={handleChange}
          warehouseId={warehouseId}
          error={error}
        />
      </MobileModalForm>
    );
  }
  return (
    <BasicDialog
      open={!!modalOpen}
      handleClose={handleModalClose}
      callback={handleSubmit}
      title='Select warehouse'
      maxWidth='xs'
    >
      <RenderInput
        handleChange={handleChange}
        warehouseId={warehouseId}
        error={error}
      />
    </BasicDialog>
  );
}

function RenderCard({
  handleSubmit,
  handleChange,
  warehouseId,
  error,
}: FormProps) {
  const { classes, cx } = useStyles();
  const warehouses = useSelector(warehousesSelectors.basicList);

  return (
    <ProcessContainer header='Select warehouse'>
      <div className={cx(classes.content, classes.card)}>
        <form onSubmit={handleSubmit} className={classes.form}>
          <SelectInput
            variant='standard'
            label='Select warehouse'
            className={classes.input}
            name='warehouseId'
            value={warehouseId}
            onChange={handleChange}
            options={warehouses}
            autoFocus
            error={Boolean(error)}
            helperText={error}
          />
          <Button variant='contained' color='primary' type='submit'>
            go
          </Button>
        </form>
      </div>
    </ProcessContainer>
  );
}

function RenderInput({ handleChange, warehouseId, error }: FormProps) {
  const { classes, css } = useStyles();
  const warehouses = useSelector(warehousesSelectors.basicList);

  return (
    <div className={classes.content}>
      <SelectInput
        variant='standard'
        label='Select warehouse'
        className={css({ width: '100%' })}
        name='warehouseId'
        value={warehouseId}
        onChange={handleChange}
        options={warehouses}
        autoFocus
        error={Boolean(error)}
        helperText={error}
      />
    </div>
  );
}

const useStyles = makeStyles({
  name: { SelectWarehouse },
})((theme) => ({
  content: {
    width: '100%',
    backgroundColor: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    height: 110,
    width: 468,
    borderRadius: 3,
    boxShadow: '0 2px 24px 0 #E5E7F5, 0 2px 40px 0 #F9FAFC',
    [theme.breakpoints.down('tablet')]: {
      width: '100%',
    },
  },
  form: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    padding: '0px 16px',
  },
  input: {
    width: '100%',
    [theme.breakpoints.down('tablet')]: {
      marginRight: 16,
    },
  },
}));
